import { EXTEND, IS, IS_NOT, nil, NO, UIRoute, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHColor } from "../Custom components/RH/RHColor"
import type { RHBreadcrumbItem } from "../Custom components/RH/RHBreadcrumbView"
import {
    isRHNavigationResettable,
    RHNavigationResettable
} from "../Custom components/RH/Utils_slop/RHNavigationResettable"
import { ProcurementSearchViewController } from "../Search/ProcurementSearchViewController"
import { NonAffiliatedMainViewController } from "./NonAffiliatedMainViewController"
import { NoRoleMainViewController } from "./NoRoleMainViewController"
import { OffererMainViewController } from "./OffererMainViewController"
import { ProcurerMainViewController } from "./ProcurerMainViewController"


/**
 * UserMainViewController — wrapper that owns the "user_main" route slot and mounts
 * either ProcurerMainViewController, OffererMainViewController, or
 * NonAffiliatedMainViewController depending on the current application mode
 * and the user's company affiliation status.
 */
export class UserMainViewController extends UIViewController implements RHNavigationResettable {

    static override readonly routeComponentName = "user_main"
    static override readonly ParameterIdentifierName = {
        tab: "tab",
        draftCategory: "draftCategory",
        offererFilter: "offererFilter"
    }
    
    readonly titleKey = "user_main"


    get breadcrumbItems(): RHBreadcrumbItem[] {
        const child = this._mountedChildViewController()
        if (child instanceof ProcurerMainViewController) {
            return child.breadcrumbItems
        }
        if (child instanceof OffererMainViewController) {
            return [{ title: "Töölaud" }]
        }
        return [{ title: "Avaleht" }]
    }
    
    readonly contentPanelView = new UIView().addedAsSubviewToView(this.view).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            if (IS(this.contentPanelView.subviews.firstElement)) {
                this.contentPanelView.subviews.firstElement.frame =
                    this.contentPanelView.bounds.rectangleWithHeight(
                        this.contentPanelView.subviews.firstElement
                    )
            }
        })
    })
    
    private _procurerMainViewController: ProcurerMainViewController | undefined
    private _offererMainViewController: OffererMainViewController | undefined
    private _nonAffiliatedMainViewController: NonAffiliatedMainViewController | undefined
    private _noRoleMainViewController: NoRoleMainViewController | undefined
    _mountGeneration = 0
    
    private get procurerMainViewController(): ProcurerMainViewController {
        if (IS_NOT(this._procurerMainViewController)) {
            this._procurerMainViewController = new ProcurerMainViewController(
                new UIView("ProcurerMainView")
            )
        }
        return this._procurerMainViewController
    }
    
    private get offererMainViewController(): OffererMainViewController {
        if (IS_NOT(this._offererMainViewController)) {
            this._offererMainViewController = new OffererMainViewController(
                new UIView("OffererMainView")
            )
        }
        return this._offererMainViewController
    }
    
    private get nonAffiliatedMainViewController(): NonAffiliatedMainViewController {
        if (IS_NOT(this._nonAffiliatedMainViewController)) {
            this._nonAffiliatedMainViewController = new NonAffiliatedMainViewController(
                new UIView("NonAffiliatedMainView")
            )
        }
        return this._nonAffiliatedMainViewController
    }
    
    private get noRoleMainViewController(): NoRoleMainViewController {
        if (IS_NOT(this._noRoleMainViewController)) {
            this._noRoleMainViewController = new NoRoleMainViewController(
                new UIView("NoRoleMainView")
            )
        }
        return this._noRoleMainViewController
    }
    
    
    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.background })
    }
    
    
    // ── Lifecycle ─────────────────────────────────────────────────────────────
    
    override async handleRoute(route: UIRoute) {
        if (IS_NOT(route.componentWithViewController(UserMainViewController))) {
            return
        }

        if (RHCore.sharedInstance.isInAuditorMode) {
            const routeWasApplied = await route
                .routeByRemovingComponentNamed(UserMainViewController.routeComponentName)
                .routeWithViewControllerComponent(ProcurementSearchViewController, nil)
                .applyByReplacingCurrentRouteInHistory()
            if (routeWasApplied) {
                return
            }
        }

        if (this._routeNeedsProcurerTabParameterRemoved(route)) {
            const routeWasApplied = await route
                .routeByRemovingParameterInComponent(
                    UserMainViewController.routeComponentName,
                    UserMainViewController.ParameterIdentifierName.tab
                )
                .applyByReplacingCurrentRouteInHistory()
            if (routeWasApplied) {
                return
            }
        }

        await this._mountCorrectChildViewController()
        await super.handleRoute(route)
    }


    _routeNeedsProcurerTabParameterRemoved(route: UIRoute): boolean {
        const modeParameter = route.componentWithName("settings")?.parameters["mode"]
        if (modeParameter !== "offerer" && modeParameter !== "auditor") {
            return NO
        }

        return IS(this._procurerTabParameterInRoute(route))
    }


    _procurerTabParameterInRoute(route: UIRoute): string | undefined {
        return route
            .componentWithViewController(UserMainViewController)
            ?.parameters[UserMainViewController.ParameterIdentifierName.tab] as string | undefined
    }
    
    
    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        
        const refreshingEventNames: string[] = [
            RHCore.broadcastEventName.applicationModeDidChange,
            RHCore.broadcastEventName.companyAffiliationsDidChange,
            RHCore.broadcastEventName.activeCompanyDidChange,
            RHCore.broadcastEventName.currentCompanyAffiliationDidChange,
            RHCore.broadcastEventName.userDidLogIn,
            RHCore.broadcastEventName.userDidLogOut
        ]
        if (refreshingEventNames.contains(event.name)) {
            if (IS_NOT(UIRoute.currentRoute.componentWithViewController(UserMainViewController))) {
                return
            }
            this._mountCorrectChildViewController().then(nil)
        }
        
    }
    
    
    // ── Child VC management ───────────────────────────────────────────────────

    async navigationWillReset(): Promise<boolean> {
        const mountedChildViewController = this._mountedChildViewController()
        if (isRHNavigationResettable(mountedChildViewController)) {
            return await mountedChildViewController.navigationWillReset()
        }
        return YES
    }


    private _mountedChildViewController(): UIViewController | undefined {
        return [
            this._procurerMainViewController,
            this._offererMainViewController,
            this._nonAffiliatedMainViewController,
            this._noRoleMainViewController
        ].find(viewController => IS(viewController) && this.hasChildViewController(viewController))
    }


    private async _mountCorrectChildViewController() {
        const mountGeneration = ++this._mountGeneration
        this.view.loading = YES
        if (!await RHCore.sharedInstance.userDataReady) {
            if (mountGeneration === this._mountGeneration) {
                this.view.loading = NO
            }
            return
        }
        const companyAffiliationStore = RHCore.sharedInstance.companyAffiliationStore
        const status = companyAffiliationStore.activeCompanyAffiliation ??
            await RHCore.sharedInstance.currentCompanyAffiliation
        if (mountGeneration !== this._mountGeneration) {
            return
        }
        const hasCompanyAffiliation = IS(companyAffiliationStore.activeCompanyAffiliation?.company)
        const isInOffererMode = RHCore.sharedInstance.isInOffererMode
        const isInAuditorMode = RHCore.sharedInstance.isInAuditorMode
        const isInProcurerMode = !isInOffererMode && !isInAuditorMode
        
        const hasProcurerRole = RHCore.sharedInstance._applicationModeIsAvailable("procurer")
        const hasOffererRole = status?.isOfferer == YES
        
        if (!hasCompanyAffiliation) {
            this._removeChildIfMounted(this._procurerMainViewController)
            this._removeChildIfMounted(this._offererMainViewController)
            this._removeChildIfMounted(this._noRoleMainViewController)
            if (!this.hasChildViewController(this.nonAffiliatedMainViewController)) {
                this.addChildViewControllerInContainer(
                    this.nonAffiliatedMainViewController,
                    this.contentPanelView
                )
                await this.nonAffiliatedMainViewController.viewWillAppear()
                if (mountGeneration === this._mountGeneration) {
                    this.nonAffiliatedMainViewController.viewDidAppear().then(nil)
                }
            }
        }
        else if (isInOffererMode && hasOffererRole) {
            this._removeChildIfMounted(this._procurerMainViewController)
            this._removeChildIfMounted(this._nonAffiliatedMainViewController)
            this._removeChildIfMounted(this._noRoleMainViewController)
            if (!this.hasChildViewController(this.offererMainViewController)) {
                this.addChildViewControllerInContainer(
                    this.offererMainViewController,
                    this.contentPanelView
                )
                await this.offererMainViewController.viewWillAppear()
                if (mountGeneration === this._mountGeneration) {
                    this.offererMainViewController.viewDidAppear().then(nil)
                }
            }
        }
        else if (isInProcurerMode && hasProcurerRole) {
            this._removeChildIfMounted(this._offererMainViewController)
            this._removeChildIfMounted(this._nonAffiliatedMainViewController)
            this._removeChildIfMounted(this._noRoleMainViewController)
            if (!this.hasChildViewController(this.procurerMainViewController)) {
                this.addChildViewControllerInContainer(
                    this.procurerMainViewController,
                    this.contentPanelView
                )
                await this.procurerMainViewController.viewWillAppear()
                if (mountGeneration === this._mountGeneration) {
                    this.procurerMainViewController.viewDidAppear().then(nil)
                }
            }
        }
        else {
            // Affiliated user with no role matching the current mode.
            this._removeChildIfMounted(this._procurerMainViewController)
            this._removeChildIfMounted(this._offererMainViewController)
            this._removeChildIfMounted(this._nonAffiliatedMainViewController)
            if (!this.hasChildViewController(this.noRoleMainViewController)) {
                this.addChildViewControllerInContainer(
                    this.noRoleMainViewController,
                    this.contentPanelView
                )
                await this.noRoleMainViewController.viewWillAppear()
                if (mountGeneration === this._mountGeneration) {
                    this.noRoleMainViewController.viewDidAppear().then(nil)
                }
            }
        }
        
        if (mountGeneration === this._mountGeneration) {
            this.view.loading = NO
            this.view.setNeedsLayoutUpToRootView()
        }
    }
    
    
    private _removeChildIfMounted(viewController: UIViewController | undefined) {
        if (IS(viewController) && this.hasChildViewController(viewController)) {
            viewController.viewWillDisappear().then(nil)
            this.removeChildViewController(viewController)
        }
    }
    
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        
        this.contentPanelView.frame = bounds.rectangleWithHeight(this.contentPanelView)
    }
    
}
